import { styled } from "@washingtonpost/wpds-ui-kit";

/** Narrow layouts (phones, small viewports) — matches controls SwipeUp threshold */
export const narrowScreen = "@media (max-width: 480px)";

export const CodeBlock = styled("pre", {
  margin: "0 0 20px",
  padding: "14px 16px",
  borderRadius: "8px",
  border: "1px solid $subtle",
  backgroundColor: "$background-forSurfaces",
  color: "$onBackground-subtle",
  fontFamily: "$meta",
  fontSize: "$075",
  lineHeight: "$160",
  whiteSpace: "pre-wrap",
  wordBreak: "break-all",
  cursor: "pointer",
  userSelect: "text",
  maxHeight: "min(380px, 50vh)",
  overflow: "auto",
  transition: "color 0.15s",
  "&:hover": {
    color: "$onBackground"
  },
  [narrowScreen]: {
    marginBottom: "16px",
    padding: "12px",
    maxHeight: "min(240px, 40vh)"
  }
});

export const SectionLabel = styled("div", {
  fontSize: "$075",
  fontWeight: "$bold",
  lineHeight: "$125",
  color: "$onBackground-subtle",
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  marginBottom: "14px"
});

export const OptionsCard = styled("div", {
  backgroundColor: "$surface",
  border: "1px solid $subtle",
  borderRadius: "8px",
  overflow: "hidden",
  marginBottom: "20px",
  [narrowScreen]: {
    marginBottom: "16px"
  }
});

export const OptRow = styled("div", {
  display: "flex",
  alignItems: "center",
  padding: "12px 14px",
  gap: "12px",
  borderTop: "1px solid $subtle",
  "&:first-child": {
    borderTop: "none"
  },
  [narrowScreen]: {
    flexWrap: "wrap",
    padding: "12px",
    gap: "10px"
  }
});

export const NativeCheckbox = styled("input", {
  appearance: "none",
  WebkitAppearance: "none",
  width: "17px",
  height: "17px",
  border: "2px solid $outline",
  borderRadius: "3px",
  cursor: "pointer",
  flexShrink: 0,
  margin: 0,
  transition: "border-color 0.15s, background 0.15s",
  backgroundColor: "transparent",
  position: "relative",
  "&:checked": {
    backgroundColor: "$cta",
    borderColor: "$cta"
  },
  "&:checked::after": {
    content: "''",
    position: "absolute",
    left: "3px",
    top: "1px",
    width: "6px",
    height: "9px",
    border: "2px solid $onCta",
    borderTop: "none",
    borderLeft: "none",
    transform: "rotate(45deg)",
    boxSizing: "border-box"
  },
  "&:focus-visible": {
    outline: "2px solid $cta",
    outlineOffset: "2px"
  }
});

export const OptLabel = styled("label", {
  fontSize: "$087",
  lineHeight: "$meta",
  color: "$onBackground",
  cursor: "pointer",
  userSelect: "none",
  flexShrink: 0
});

export const TimeInput = styled("input", {
  width: "68px",
  boxSizing: "border-box",
  backgroundColor: "$background-forSurfaces",
  border: "1px solid $outline-subtle",
  borderRadius: "5px",
  color: "$onBackground",
  fontSize: "$087",
  padding: "5px 10px",
  textAlign: "center",
  outline: "none",
  transition: "border-color 0.15s, opacity 0.15s",
  "&:focus": {
    borderColor: "$cta"
  },
  variants: {
    inactive: {
      true: { opacity: 0.38 },
      false: { opacity: 1 }
    }
  },
  defaultVariants: {
    inactive: false
  }
});

export const PresetRow = styled("div", {
  display: "flex",
  alignItems: "center",
  gap: "8px",
  padding: "10px 14px",
  borderTop: "1px solid $subtle",
  [narrowScreen]: {
    flexDirection: "column",
    alignItems: "stretch",
    gap: "6px",
    padding: "10px 12px"
  }
});

export const PresetChipsGroup = styled("div", {
  display: "flex",
  alignItems: "center",
  gap: "8px",
  flex: 1,
  minWidth: 0,
  [narrowScreen]: {
    width: "100%"
  }
});

export const PresetLabel = styled("span", {
  fontSize: "$075",
  lineHeight: "$125",
  color: "$onBackground-subtle",
  fontWeight: 500,
  flexShrink: 0,
  marginRight: "2px",
  [narrowScreen]: {
    marginRight: 0
  }
});

export const Chip = styled("button", {
  all: "unset",
  boxSizing: "border-box",
  fontSize: "$075",
  lineHeight: "$125",
  color: "$onBackground-subtle",
  backgroundColor: "$background-forSurfaces",
  border: "1px solid $outline-subtle",
  borderRadius: "4px",
  padding: "4px 10px",
  cursor: "pointer",
  transition: "background 0.12s, border-color 0.12s, color 0.12s",
  userSelect: "none",
  whiteSpace: "nowrap",
  flex: 1,
  textAlign: "center",
  "&:hover": {
    backgroundColor: "$surface-highest",
    borderColor: "$outline",
    color: "$onBackground"
  },
  "&:focus-visible": {
    outline: "2px solid $cta",
    outlineOffset: "2px"
  },
  variants: {
    active: {
      true: {
        backgroundColor: "$ctaContainer",
        borderColor: "$cta",
        color: "$cta"
      },
      false: {}
    }
  },
  defaultVariants: {
    active: false
  },
  [narrowScreen]: {
    padding: "4px 6px"
  }
});

export const DimRow = styled("div", {
  display: "flex",
  alignItems: "center",
  gap: 0,
  padding: "10px 14px",
  borderTop: "1px solid $subtle",
  [narrowScreen]: {
    flexDirection: "column",
    alignItems: "stretch",
    gap: "8px",
    padding: "10px 12px"
  }
});

export const DimField = styled("div", {
  display: "flex",
  alignItems: "center",
  backgroundColor: "$background-forSurfaces",
  border: "1px solid $outline-subtle",
  borderRadius: "5px",
  overflow: "hidden",
  flex: 1,
  minWidth: 0,
  transition: "border-color 0.15s",
  "&:focus-within": {
    borderColor: "$cta"
  }
});

export const DimTag = styled("span", {
  fontSize: "$075",
  fontWeight: "$bold",
  lineHeight: "$125",
  color: "$onBackground-subtle",
  letterSpacing: "0.05em",
  padding: "0 8px",
  height: "34px",
  display: "flex",
  alignItems: "center",
  backgroundColor: "$surface",
  borderRight: "1px solid $outline-subtle",
  userSelect: "none",
  flexShrink: 0
});

export const DimInput = styled("input", {
  flex: 1,
  minWidth: 0,
  height: "34px",
  border: "none",
  backgroundColor: "transparent",
  color: "$onBackground",
  fontSize: "$087",
  padding: "0 8px",
  textAlign: "center",
  outline: "none",
  boxSizing: "border-box"
});

export const DimSep = styled("span", {
  fontSize: "$112",
  lineHeight: "$125",
  color: "$onBackground-subtle",
  padding: "0 10px",
  flexShrink: 0,
  userSelect: "none",
  [narrowScreen]: {
    display: "none"
  }
});

export const DividerLine = styled("div", {
  height: "1px",
  backgroundColor: "$subtle",
  margin: "0 0 20px",
  [narrowScreen]: {
    marginBottom: "16px"
  }
});

export const CopyButton = styled("button", {
  all: "unset",
  boxSizing: "border-box",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "8px",
  width: "100%",
  backgroundColor: "$cta",
  color: "$onCta",
  borderRadius: "20px",
  padding: "10px 22px",
  fontSize: "$087",
  fontWeight: "$bold",
  lineHeight: "$125",
  cursor: "pointer",
  letterSpacing: "0.01em",
  transition: "background 0.15s, transform 0.1s",
  "& svg": {
    width: "16px",
    height: "16px",
    flexShrink: 0
  },
  "&:hover": {
    color: "$onCta-hover"
  },
  "&:active": {
    transform: "scale(0.98)"
  },
  variants: {
    copied: {
      true: {
        backgroundColor: "$success"
      }
    }
  },
  defaultVariants: {
    copied: false
  },
  "&:focus-visible": {
    outline: "2px solid $cta",
    outlineOffset: "2px"
  }
});

export const CloseButton = styled("button", {
  all: "unset",
  boxSizing: "border-box",
  width: "32px",
  height: "32px",
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  color: "$onBackground-subtle",
  transition: "background 0.15s, color 0.15s",
  "&:hover": {
    backgroundColor: "$subtle",
    color: "$onBackground"
  },
  "& svg": {
    width: "18px",
    height: "18px"
  },
  "&:focus-visible": {
    outline: "2px solid $cta",
    outlineOffset: "2px"
  }
});

export const HeaderRow = styled("div", {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  marginBottom: "20px",
  [narrowScreen]: {
    marginBottom: "16px"
  }
});
